import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  map,
  mergeMap,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { environment } from '../../environments/environment';
import { UserResDto } from '../_dtos/userResDto';
import { FeedbackReqDto } from '../_dtos/feedbackReqDto';

interface ProvisionedPlan {
  service: string;
  capabilityStatus: string;
  provisioningStatus: string;
}

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  private readonly apiUrlDirectoryObjects = `${environment.graphBaseUrl}/directoryObjects`;
  private readonly apiUrlMe = `${environment.graphBaseUrl}/me`;
  private readonly apiUrlGroups = `${environment.graphBaseUrl}/groups`;
  private readonly apiUrlUsers = `${environment.graphBaseUrl}/users`;
  private readonly apiUrlSPItem = `${environment.graphBaseUrl}/sites`;

  private userType = new BehaviorSubject<string | null>(null);
  private city = new BehaviorSubject<string | null>(null);
  private postalCode = new BehaviorSubject<string | null>(null);

  defaultUserType$ = this.userType.asObservable();
  city$ = this.city.asObservable(); // Used in Bookings
  postalCode$ = this.postalCode.asObservable(); // Used in Bookings

  constructor(private http: HttpClient) {}

  validateMailNickname(mailNickname: string): Observable<boolean> {
    const body = {
      entityType: 'Group',
      mailNickname: mailNickname,
    };

    const urlDirectoryObjects =
      this.apiUrlDirectoryObjects + '/microsoft.graph.validateProperties';

    return this.http
      .post<any>(urlDirectoryObjects, body, { observe: 'response' })
      .pipe(
        map((response) => {
          return response.status === 204;
        }),
        catchError((error) => {
          if (error.status === 422) return of(false);

          return throwError(error);
        }),
      );
  }

  // A 404 means the UPN is free; the error interceptor lets 404 of /users through without a toast
  isUserPrincipalNameFree(userPrincipalName: string): Observable<boolean> {
    const url = `${this.apiUrlUsers}/${encodeURIComponent(userPrincipalName)}?$select=id`;

    return this.http.get<any>(url).pipe(
      map(() => false),
      catchError((error) => {
        if (error.status === 404) return of(true);

        return throwError(() => error);
      }),
    );
  }

  // Provisioned Exchange plan: mail and proxyAddresses are also set for accounts without a mailbox
  hasMailbox(userId: string): Observable<boolean> {
    const url = `${this.apiUrlUsers}/${encodeURIComponent(userId)}?$select=provisionedPlans`;

    return this.http
      .get<{ provisionedPlans?: ProvisionedPlan[] }>(url)
      .pipe(
        map((user) =>
          (user.provisionedPlans ?? []).some(
            (plan) =>
              plan.service === 'exchange' &&
              plan.capabilityStatus === 'Enabled' &&
              plan.provisioningStatus === 'Success',
          ),
        ),
      );
  }

  getExtensionAttributes(): Observable<any> {
    const url = this.apiUrlMe + '?$select=onPremisesExtensionAttributes';
    return this.http.get<any>(url);
  }

  getUserInformation(): Observable<any> {
    const url =
      this.apiUrlMe + '?$select=city,postalCode,userPrincipalName,email';
    return this.http.get<any>(url);
  }

  getOwnedObjects(): Observable<any> {
    const url = `${this.apiUrlMe}/ownedObjects?$select=id,displayName,mailNickname,resourceProvisioningOptions`;
    return this.fetchAllPages(url, []);
  }

  getMembers(groupId: string): Observable<any> {
    const url = this.apiUrlGroups + '/' + groupId + '/members?$select=id';
    return this.http.get<any>(url);
  }

  getOwners(groupId: string): Observable<any> {
    const url = this.apiUrlGroups + '/' + groupId + '/owners?$select=id';
    return this.http.get<any>(url);
  }

  searchPeople(value: string): Observable<UserResDto[]> {
    if (!value.trim()) return of([]);

    const sanitizedValue = this.sanitizeSearchInput(value.trim());
    const encodedValue = this.customEncode(`'${sanitizedValue}'`);
    const filter =
      `startswith(displayName, ${encodedValue}) or ` +
      `startswith(givenName, ${encodedValue}) or ` +
      `startswith(surname, ${encodedValue}) or ` +
      `startswith(mail, ${encodedValue}) or ` +
      `startswith(userPrincipalName, ${encodedValue})`;

    const params = new HttpParams()
      .set('$filter', filter)
      .set('$select', 'id,displayName,userPrincipalName,mailNickname');

    return this.http
      .get<{ value: UserResDto[] }>(this.apiUrlUsers, { params })
      .pipe(map((response) => response.value));
  }

  getUserByUpn(upn: string): Observable<UserResDto[]> {
    const formattedUpn = upn.trim();
    const filter = `userPrincipalName eq '${formattedUpn}'`;
    const encodedFilter = encodeURIComponent(filter);

    return this.http
      .get<{
        value: UserResDto[];
      }>(
        `${this.apiUrlUsers}?$filter=${encodedFilter}&$select=id,displayName,userPrincipalName,mailNickname`,
      )
      .pipe(
        map((response) => {
          if (response.value && response.value.length > 0) {
            return [response.value[0]];
          }
          return [];
        }),
      );
  }

  searchGroupOwner(value: string, id: string): Observable<UserResDto[]> {
    if (!value.trim()) {
      return of([]);
    }

    const sanitizedValue = this.sanitizeSearchInput(value.trim()).toLowerCase();

    const url = `${this.apiUrlGroups}/${id}/owners`;

    return this.http.get<{ value: UserResDto[] }>(url).pipe(
      map((response) => {
        const owners = response.value || [];
        return owners.filter((owner) => {
          return (
            owner.displayName?.toLowerCase().startsWith(sanitizedValue) ||
            owner.userPrincipalName?.toLowerCase().startsWith(sanitizedValue) ||
            owner.mailNickname?.toLowerCase().startsWith(sanitizedValue) ||
            owner.givenName?.toLowerCase().startsWith(sanitizedValue)
          );
        });
      }),
    );
  }

  private customEncode(value: string): string {
    return (
      encodeURIComponent(value)
        .replace(/%20/g, ' ')
        // German
        .replace(/%C3%A4/g, 'ä')
        .replace(/%C3%B6/g, 'ö')
        .replace(/%C3%BC/g, 'ü')
        .replace(/%C3%84/g, 'Ä')
        .replace(/%C3%96/g, 'Ö')
        .replace(/%C3%9C/g, 'Ü')
        .replace(/%C3%9F/g, 'ß')
        // French and other Latin-1 Supplement characters
        .replace(/%C3%A1/g, 'á')
        .replace(/%C3%A9/g, 'é')
        .replace(/%C3%AD/g, 'í')
        .replace(/%C3%B3/g, 'ó')
        .replace(/%C3%BA/g, 'ú')
        .replace(/%C3%A0/g, 'à')
        .replace(/%C3%A8/g, 'è')
        .replace(/%C3%AC/g, 'ì')
        .replace(/%C3%B2/g, 'ò')
        .replace(/%C3%B9/g, 'ù')
        .replace(/%C3%A2/g, 'â')
        .replace(/%C3%AA/g, 'ê')
        .replace(/%C3%AE/g, 'î')
        .replace(/%C3%B4/g, 'ô')
        .replace(/%C3%BB/g, 'û')
        .replace(/%C3%A3/g, 'ã')
        .replace(/%C3%B5/g, 'õ')
        .replace(/%C3%B1/g, 'ñ')
        .replace(/%C3%A7/g, 'ç')
        // Uppercase variants
        .replace(/%C3%81/g, 'Á')
        .replace(/%C3%89/g, 'É')
        .replace(/%C3%8D/g, 'Í')
        .replace(/%C3%93/g, 'Ó')
        .replace(/%C3%9A/g, 'Ú')
        .replace(/%C3%80/g, 'À')
        .replace(/%C3%88/g, 'È')
        .replace(/%C3%8C/g, 'Ì')
        .replace(/%C3%92/g, 'Ò')
        .replace(/%C3%99/g, 'Ù')
        .replace(/%C3%82/g, 'Â')
        .replace(/%C3%8A/g, 'Ê')
        .replace(/%C3%8E/g, 'Î')
        .replace(/%C3%94/g, 'Ô')
        .replace(/%C3%9B/g, 'Û')
        .replace(/%C3%83/g, 'Ã')
        .replace(/%C3%95/g, 'Õ')
        .replace(/%C3%91/g, 'Ñ')
        .replace(/%C3%87/g, 'Ç')
    );
  }

  private sanitizeSearchInput(input: string): string {
    return input.replace(/'/g, "''");
  }

  createSPItem(feedbackRequestDto: FeedbackReqDto) {
    const url =
      this.apiUrlSPItem +
      '/106b5742-780b-4a68-ba2c-ad8b93b4def9/lists/30C862F1-5936-43E4-B948-13030943F37A/items';
    const body = {
      fields: {
        Feedback: feedbackRequestDto.Feedback,
        BernieRDMVersion: feedbackRequestDto.BernieRDMVersion,
      },
    };
    return this.http.post(url, body);
  }

  setDefaultUserType(userType: string) {
    this.userType.next(userType);
  }

  setCity(city: string) {
    this.city.next(city);
  }

  setPostalCode(postalCode: string) {
    this.postalCode.next(postalCode);
  }

  getUserType(): string {
    const userType = this.userType.getValue();
    if (!userType)
      throw new Error('Attempted to access userType before it was loaded');

    return userType;
  }

  getCity(): string {
    const city = this.city.getValue();
    if (!city) throw new Error('Attempted to access city before it was loaded');

    return city;
  }

  getPostalCode(): string {
    const pCode = this.postalCode.getValue();
    if (!pCode)
      throw new Error('Attempted to access postalCode before it was loaded');

    return pCode;
  }

  private fetchAllPages(url: string, collected: any[]): Observable<any[]> {
    return this.http.get<any>(url).pipe(
      mergeMap((response) => {
        const newItems = response.value || [];
        const allItems = [...collected, ...newItems];
        if (response['@odata.nextLink']) {
          return this.fetchAllPages(response['@odata.nextLink'], allItems);
        } else {
          return of(allItems);
        }
      }),
    );
  }
}
